import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
// BlueprintProvider composes Blueprint's Portal/Overlays/Hotkeys providers in
// one wrapper. Its built-in HotkeysProvider is what makes app-wide `useHotkeys`
// (the ⌘J palette, ⌘⇧U IRIS toggle, …) register into — and auto-render — the
// global hotkeys help dialog (opened with `?`). It must be the ONLY hotkeys
// provider in the tree: Blueprint warns that nesting a second one breaks global
// hotkey registration, so we deliberately do not add a standalone
// <HotkeysProvider> around <App/> — BlueprintProvider already is one.
import { BlueprintProvider, FocusStyleManager } from "@blueprintjs/core";
import { Icons, getIconPaths } from "@blueprintjs/icons";

// Vendor CSS (normalize + Blueprint) routed through cascade layers (reset <
// blueprint) so app's `@layer ironcase` rules win without !important.
import "./styles/vendor/blueprint.css";
import "./styles/index.scss";

import { App } from "./App";
import { AuthProvider } from "./contexts/auth-context";
import { ThemeProvider } from "./contexts/theme-context";
import { setTokenGetter } from "./lib/api";
import { currentSession } from "./lib/auth";
import { hydrateFavoritesFromServer } from "./lib/favorites-sync";
import { initializeWorkspaceSession } from "./lib/workspace-session";

FocusStyleManager.onlyShowFocusOnTabs();
// Match Workspace's emitted page/tab correlation mechanism: a new page UUID
// on every document boot and one tab UUID retained for this sessionStorage
// lifetime. These are telemetry correlation IDs, never authentication state.
initializeWorkspaceSession(window.sessionStorage);
// Statically bundle every Blueprint icon's path data into the main JS bundle
// (via getIconPaths, the documented static-loader API) and pre-populate the
// loader cache before the React tree mounts. Eliminates icon FOUC entirely —
// no dynamic chunks, no async loader module, every <Icon> renders sync from
// the cache on first paint.
Icons.setLoaderOptions({
  loader: (name, size) => Promise.resolve(getIconPaths(name, size)),
});
await Icons.loadAll();

// Bridge: API client → Cognito session. Reads the current ID token at
// request time so refreshed tokens propagate transparently. We synchronously
// pull from localStorage via the Cognito SDK; the lib handles refresh on
// expired access tokens via getSession() in the auth lib.
let cachedToken: string | null = null;
setTokenGetter(() => cachedToken);
void currentSession().then((s) => {
  cachedToken = s?.idToken ?? null;
  // Once the token resolves, hydrate favorites from the server (idempotent —
  // the localStorage cache already provided the first paint).
  if (cachedToken) void hydrateFavoritesFromServer();
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const root = document.getElementById("root");
if (!root) throw new Error("#root not found");

createRoot(root).render(
  <StrictMode>
    <ThemeProvider>
      <BlueprintProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <AuthProvider>
              <TokenBridge />
              <App />
            </AuthProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </BlueprintProvider>
    </ThemeProvider>
  </StrictMode>,
);

// Inside the provider so it can react to auth state changes via the context.
import { useEffect } from "react";
import { useAuth } from "./contexts/auth-context";
import { useQueryClient } from "@tanstack/react-query";

function TokenBridge() {
  const { session, status } = useAuth();
  const qc = useQueryClient();
  useEffect(() => {
    cachedToken = session?.idToken ?? null;
    // Re-fetch any active queries when the session changes (login/logout).
    if (status === "authenticated" || status === "unauthenticated") {
      qc.invalidateQueries();
    }
    // Hydrate favorites after an interactive sign-in (a fresh browser context
    // has no module-load session, so the token only arrives here). Idempotent.
    if (status === "authenticated" && cachedToken) void hydrateFavoritesFromServer();
  }, [session?.idToken, status, qc]);
  return null;
}
